import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, eachDayOfInterval, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  CheckSquare, 
  Calendar,
  ChevronRight,
  ChevronLeft,
  User,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { checklistAssignmentsApi, checklistInstancesApi, ChecklistAssignment, ChecklistItemInstance, usersApi, projectsApi } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useTelegram } from '../hooks/useTelegram';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { SubordinatesChecklists } from '../components/SubordinatesChecklists';
import { ChecklistApprovalsPage } from './ChecklistApprovalsPage';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { isChecklistInstanceOverdue } from '../utils/overdue';
import { useLogger } from '../utils/logger';
import { LoadingSpinner } from '../components/LoadingScreen';

type FilterType = 'all' | 'today' | 'overdue' | 'pending' | 'accepted' | 'rejected' | 'completed';
type MainSection = 'my' | 'subordinates' | 'approvals';

export function ChecklistsPage() {
  const logger = useLogger('ChecklistsPage');
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const currentUser = useAuthStore((state: any) => state.user);
  const [mainSection, setMainSection] = useState<MainSection>('my');
  
  const [filter, setFilter] = useState<FilterType>('all');
  const [activeTab, setActiveTab] = useState<'regulation' | 'event' | 'personal'>('personal');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  useEffect(() => {
    logger.componentMount({ mainSection });
    return () => {
      logger.componentUnmount();
    };
  }, []);

  // Получаем данные пользователя из API для актуальной информации
  // Принудительно обновляем данные при загрузке страницы
  const { data: userData } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      logger.apiCall('GET', '/users/me');
      try {
        const data = await usersApi.me();
        logger.apiSuccess('GET', '/users/me', data);
        return data;
      } catch (error: any) {
        logger.apiError('GET', '/users/me', error);
        throw error;
      }
    },
    staleTime: 0, // Всегда считаем данные устаревшими
    gcTime: 0, // Не кешируем
    refetchOnMount: true, // Всегда обновляем при монтировании
    refetchOnWindowFocus: true, // Обновляем при фокусе окна
  });

  const user = userData || currentUser;
  

  // Проверяем, является ли пользователь руководителем в каком-либо проекте
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      logger.apiCall('GET', '/projects');
      try {
        const data = await projectsApi.list();
        logger.apiSuccess('GET', '/projects', data);
        return data;
      } catch (error: any) {
        logger.apiError('GET', '/projects', error);
        throw error;
      }
    },
  });

  // Системный админ или менеджер в проектах
  // Важно: проверяем isSystemAdmin строго на true
  // Проверяем и userData, и currentUser на случай, если данные еще не обновились
  // Также проверяем роль SYSTEM_ADMIN в проектах для системных админов
  const isSystemAdminFromData = Boolean((userData as any)?.isSystemAdmin);
  const isSystemAdminFromStore = Boolean((currentUser as any)?.isSystemAdmin);
  const hasSystemAdminRole = projects?.some((p: any) => p.role === 'SYSTEM_ADMIN') || false;
  const isSystemAdmin = isSystemAdminFromData || isSystemAdminFromStore || hasSystemAdminRole;
  const hasManagerRole = projects?.some((p: any) => ['ADMIN', 'MANAGER'].includes(p.role)) || false;
  const isManager = isSystemAdmin || hasManagerRole;
  
  useEffect(() => {
    logger.debug('User and role data updated', {
      isSystemAdminFromData,
      isSystemAdminFromStore,
      isSystemAdmin,
      hasManagerRole,
      isManager,
      projectsCount: projects?.length,
      userId: user?.id,
    });
  }, [isSystemAdmin, isManager, projects, user]);

  // Получаем назначения
  const { data: assignments, refetch: refetchAssignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['checklist-assignments', user?.id, mainSection],
    queryFn: async () => {
      const userId = user?.id;
      if (!userId) {
        return [];
      }

      if (mainSection === 'my') {
        const params = { status: 'ACTIVE', assigneeId: userId };
        const data = await checklistAssignmentsApi.list(params);
        if (!Array.isArray(data)) return [];
        return data.filter((a: any) => a && a.template && a.template.id);
      } else {
        const params = { status: 'ACTIVE' };
        const data = await checklistAssignmentsApi.list(params);
        if (!Array.isArray(data)) return [];
        return data.filter((a: any) => a && a.template && a.template.id);
      }
    },
    enabled: !!user?.id && mainSection !== undefined,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0, // Не кешируем, всегда получаем свежие данные
    gcTime: 0, // Не храним в кеше
  });

  // Обновляем назначения при изменении раздела и при возврате на страницу
  useEffect(() => {
    if (user?.id && mainSection === 'my') {
      refetchAssignments();
    }
  }, [user?.id, mainSection, refetchAssignments]);

  // Обновляем данные при фокусе окна (когда пользователь возвращается на страницу)
  useEffect(() => {
    const handleFocus = () => {
      if (user?.id && mainSection === 'my') {
        refetchAssignments();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id, mainSection, refetchAssignments]);

  // Загружаем экземпляры для фильтрации
  const { data: allInstances } = useQuery({
    queryKey: ['checklist-instances-all'],
    queryFn: async () => {
      logger.apiCall('GET', '/checklist-instances');
      try {
        const data = await checklistInstancesApi.list({});
        logger.apiSuccess('GET', '/checklist-instances', data);
        // Дедупликация по ID (на случай дубликатов в базе)
        const uniqueInstances = Array.isArray(data) 
          ? Array.from(new Map(data.map((inst: ChecklistItemInstance) => [inst.id, inst])).values())
          : [];
        return uniqueInstances;
      } catch (error: any) {
        logger.apiError('GET', '/checklist-instances', error);
        throw error;
      }
    },
  });

  // Пока не загрузились данные, показываем только спиннер
  // Вкладки будут видны после загрузки

  const [eventFilter, setEventFilter] = useState<'active' | 'archived'>('active');
  
  // Фильтрация по типам
  const regulationAssignments = useMemo(() => {
    if (!Array.isArray(assignments) || assignments.length === 0) return [];
    return assignments.filter((a: any) => a?.template?.type === 'REGULATION');
  }, [assignments]);

  const eventAssignments = useMemo(() => {
    if (!Array.isArray(assignments) || assignments.length === 0) return [];
    return assignments.filter((a: any) => {
      if (a?.template?.type !== 'EVENT') return false;
      if (eventFilter === 'active') return a.status === 'ACTIVE';
      if (eventFilter === 'archived') return a.status === 'ARCHIVED';
      return true;
    });
  }, [assignments, eventFilter]);
  
  // Фильтруем личные чек-листы
  const personalAssignments = useMemo(() => {
    if (!Array.isArray(assignments) || assignments.length === 0) {
      return [];
    }
    const personal = assignments.filter((a: any) => a?.template?.type === 'PERSONAL');
    return personal;
  }, [assignments]);
  

  const getInstancesForAssignment = (assignmentId: string): ChecklistItemInstance[] => {
    if (!allInstances || !Array.isArray(allInstances)) return [];
    // Фильтруем по assignmentId и дедуплицируем по ID
    const filtered = allInstances.filter((inst: ChecklistItemInstance) => inst.assignmentId === assignmentId);
    // Дедупликация по ID (на случай дубликатов)
    const unique = Array.from(new Map(filtered.map((inst) => [inst.id, inst])).values());
    // Сортируем по дате планирования
    return unique.sort((a, b) => {
      const dateA = new Date(a.plannedDate).getTime();
      const dateB = new Date(b.plannedDate).getTime();
      return dateA - dateB;
    });
  };

  const filterInstances = (instances: ChecklistItemInstance[]): ChecklistItemInstance[] => {
    if (filter === 'all') return instances;
    
    const now = new Date();
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);

    return instances.filter((inst: ChecklistItemInstance) => {
      switch (filter) {
        case 'today':
          const instDate = new Date(inst.plannedDate);
          return instDate >= todayStart && instDate <= todayEnd;
        case 'overdue':
          return isChecklistInstanceOverdue(inst);
        case 'pending':
          return inst.status === 'COMPLETED_PENDING';
        case 'accepted':
          return inst.status === 'ACCEPTED';
        case 'rejected':
          return inst.status === 'REJECTED';
        case 'completed':
          return inst.status === 'COMPLETED' || inst.status === 'ACCEPTED';
        default:
          return true;
      }
    });
  };

  const getCurrentAssignments = () => {
    let assignments: ChecklistAssignment[] = [];
    switch (activeTab) {
      case 'regulation':
        assignments = regulationAssignments;
        break;
      case 'event':
        assignments = eventAssignments;
        break;
      case 'personal':
        assignments = personalAssignments;
        break;
    }

    // Для личных чек-листов показываем все назначения, даже если нет экземпляров
    // ВАЖНО: не фильтруем по экземплярам для личных чек-листов
    if (activeTab === 'personal') {
      // Возвращаем все личные назначения без фильтрации по экземплярам
      return assignments;
    }

    // Для других типов: если фильтр не "all", фильтруем по экземплярам
    if (filter !== 'all' && allInstances) {
      return assignments.filter(assignment => {
        const instances = getInstancesForAssignment(assignment.id);
        return filterInstances(instances).length > 0;
      });
    }

    return assignments;
  };

  // Генерация календаря
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getInitials = (firstName: string, lastName: string | null) => {
    const first = firstName?.[0]?.toUpperCase() || '';
    const last = lastName?.[0]?.toUpperCase() || '';
    return (first + last).slice(0, 2) || '?';
  };

  const getTasksForDate = (date: Date) => {
    if (!allInstances) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return allInstances.filter((inst: ChecklistItemInstance) => format(new Date(inst.plannedDate), 'yyyy-MM-dd') === dateStr);
  };

  // Компонент вкладок для переиспользования (ВСЕГДА видимые, КРИТИЧЕСКИ ВАЖНО!)
  const renderSectionTabs = () => {
    logger.debug('Rendering section tabs', { isManager, mainSection });
    return (
      <div 
        className="flex gap-2 overflow-x-auto mb-4 scrollbar-hide"
        style={{ 
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'x mandatory',
          scrollPaddingLeft: '12px',
          scrollPaddingRight: '12px',
        }}
      >
        <button
          onClick={() => {
            logger.userAction('section_tab_click', { section: 'my' });
            setMainSection('my');
            hapticFeedback('light');
          }}
          style={{ scrollSnapAlign: 'start' }}
          className={`
            flex-shrink-0 font-medium rounded-lg px-4 py-2
            transition-all duration-200 active:scale-[0.95]
            min-h-[40px] touch-manipulation whitespace-nowrap
            text-sm
            ${mainSection === 'my' 
              ? 'bg-blue-500 text-white shadow-md font-semibold' 
              : 'bg-gray-100 text-gray-700'
            }
          `}
        >
          <CheckSquare className="w-4 h-4 inline mr-1.5" />
          Мои чек-листы
        </button>
        
        {/* ВАЖНО: Вкладки видны ВСЕГДА, но для не-менеджеров они неактивны */}
        <button
          onClick={() => {
            if (isManager) {
              logger.userAction('section_tab_click', { section: 'subordinates' });
              setMainSection('subordinates');
              hapticFeedback('light');
            } else {
              hapticFeedback('error');
            }
          }}
          style={{ scrollSnapAlign: 'start' }}
          disabled={!isManager}
          className={`
            flex-shrink-0 font-medium rounded-lg px-4 py-2
            transition-all duration-200 active:scale-[0.95]
            min-h-[40px] touch-manipulation whitespace-nowrap
            text-sm
            ${!isManager ? 'opacity-50 cursor-not-allowed' : ''}
            ${mainSection === 'subordinates' 
              ? 'bg-blue-500 text-white shadow-md font-semibold' 
              : 'bg-gray-100 text-gray-700'
            }
          `}
        >
          <User className="w-4 h-4 inline mr-1.5" />
          Подчиненных
        </button>
        
        <button
          onClick={() => {
            if (isManager) {
              logger.userAction('section_tab_click', { section: 'approvals' });
              setMainSection('approvals');
              hapticFeedback('light');
            } else {
              hapticFeedback('error');
            }
          }}
          style={{ scrollSnapAlign: 'start' }}
          disabled={!isManager}
          className={`
            flex-shrink-0 font-medium rounded-lg px-4 py-2
            transition-all duration-200 active:scale-[0.95]
            min-h-[40px] touch-manipulation whitespace-nowrap
            text-sm
            ${!isManager ? 'opacity-50 cursor-not-allowed' : ''}
            ${mainSection === 'approvals' 
              ? 'bg-blue-500 text-white shadow-md font-semibold' 
              : 'bg-gray-100 text-gray-700'
            }
          `}
        >
          <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
          Согласование
        </button>
      </div>
    );
  };

  // Рендер раздела "Мои чек-листы"
  const renderMyChecklists = () => (
    <div className="pb-24">
      {/* Профиль пользователя */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            {user?.photoUrl ? (
              <img 
                src={user.photoUrl} 
                alt={user.firstName}
                className="w-12 h-12 rounded-xl object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center text-white font-bold text-lg">
                {getInitials(user?.firstName || '', user?.lastName || null)}
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-blue-400 rounded-full border-2 border-white flex items-center justify-center">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-lg">
                {user?.lastName || ''} {user?.firstName}
              </h2>
              <span className="text-blue-200">?</span>
            </div>
          </div>
          <div className="text-sm text-blue-100">сейчас</div>
        </div>
      </div>

      <div className="px-4 space-y-4">
        {/* Вкладки основных разделов - показываем сразу после профиля с отступом */}
        <div className="pt-4">
          {renderSectionTabs()}
        </div>
        
        {/* 1. Календарь (компактный, одна строка) */}
        <Card className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              <h3 className="font-semibold text-sm text-tg-text">Календарь</h3>
            </div>
            <button
              onClick={() => {
                navigate('/checklists/calendar');
                hapticFeedback('light');
              }}
              className="text-blue-500"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          {/* Компактный календарь - одна строка с датами */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => {
                setCurrentMonth(subMonths(currentMonth, 1));
                hapticFeedback('light');
              }}
              className="flex-shrink-0 p-1"
            >
              <ChevronLeft className="w-4 h-4 text-gray-500" />
            </button>
            
            {/* Текущая неделя */}
            {calendarDays.slice(0, 7).map((day, idx) => {
              const isCurrentMonth = format(day, 'M') === format(currentMonth, 'M');
              const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
              const isSelected = format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
              const tasksCount = getTasksForDate(day).length;
              
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedDate(day);
                    hapticFeedback('light');
                  }}
                  className={`
                    flex-shrink-0 w-10 h-10 rounded-lg text-xs font-medium transition-all
                    ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                    ${isToday ? 'bg-blue-500 text-white font-bold' : ''}
                    ${isSelected && !isToday ? 'bg-blue-100 text-blue-700' : ''}
                    ${!isToday && !isSelected && isCurrentMonth ? 'hover:bg-gray-100' : ''}
                    flex flex-col items-center justify-center relative
                  `}
                >
                  <span className="text-[10px] text-gray-400 mb-0.5">
                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'][idx]}
                  </span>
                  <span>{format(day, 'd')}</span>
                  {tasksCount > 0 && (
                    <div className="absolute bottom-1 w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  )}
                </button>
              );
            })}
            
            <button
              onClick={() => {
                setCurrentMonth(addMonths(currentMonth, 1));
                hapticFeedback('light');
              }}
              className="flex-shrink-0 p-1"
            >
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </Card>

        {/* 2. Виды чек-листов (Табы) */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-tg-text flex items-center gap-2">
            <CheckSquare className="w-5 h-5" />
            Мои чек-листы
          </h2>
        </div>
        
        {/* Если менеджер, но вкладки не видны - показываем подсказку */}
        {!isManager && (userData || projects) && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
            <p className="text-sm text-yellow-800">
              Для создания чек-листов подчинённым необходимы права руководителя или системного администратора.
            </p>
          </div>
        )}
        
        {/* Табы - Виды чек-листов */}
        <div 
          className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4"
          style={{ 
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x mandatory',
            scrollPaddingLeft: '16px',
            scrollPaddingRight: '16px',
          }}
        >
        <button
          onClick={() => {
            logger.userAction('checklist_tab_click', { tab: 'regulation' });
            setActiveTab('regulation');
            hapticFeedback('light');
          }}
          style={{ scrollSnapAlign: 'start' }}
          className={`
            flex-shrink-0 font-medium rounded-lg px-3 py-2
            transition-all duration-200 active:scale-[0.95]
            min-h-[36px] touch-manipulation whitespace-nowrap
            text-sm
            ${activeTab === 'regulation' 
              ? 'bg-blue-500 text-white shadow-md font-semibold' 
              : 'bg-white text-gray-700 border border-gray-200 active:bg-gray-50'
            }
          `}
        >
          Регламентные
        </button>
        <button
          onClick={() => {
            logger.userAction('checklist_tab_click', { tab: 'event' });
            setActiveTab('event');
            hapticFeedback('light');
          }}
          style={{ scrollSnapAlign: 'start' }}
          className={`
            flex-shrink-0 font-medium rounded-lg px-3 py-2
            transition-all duration-200 active:scale-[0.95]
            min-h-[36px] touch-manipulation whitespace-nowrap
            text-sm
            ${activeTab === 'event' 
              ? 'bg-blue-500 text-white shadow-md font-semibold' 
              : 'bg-white text-gray-700 border border-gray-200 active:bg-gray-50'
            }
          `}
        >
          Мероприятия
        </button>
        <button
          onClick={() => {
            setActiveTab('personal');
            hapticFeedback('light');
          }}
          style={{ scrollSnapAlign: 'start' }}
          className={`
            flex-shrink-0 font-medium rounded-lg px-3 py-2
            transition-all duration-200 active:scale-[0.95]
            min-h-[36px] touch-manipulation whitespace-nowrap
            text-sm
            ${activeTab === 'personal' 
              ? 'bg-blue-500 text-white shadow-md font-semibold' 
              : 'bg-white text-gray-700 border border-gray-200 active:bg-gray-50'
            }
          `}
        >
          Личные
        </button>
        </div>

        {/* Фильтр для мероприятий: Активные / Архив */}
        {activeTab === 'event' && (
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => {
                setEventFilter('active');
                hapticFeedback('light');
              }}
              className={`
                flex-1 px-3 py-2 rounded-lg font-medium text-sm
                transition-all duration-200 active:scale-[0.95]
                ${eventFilter === 'active'
                  ? 'bg-blue-500 text-white shadow-md font-semibold'
                  : 'bg-white text-gray-700 border border-gray-200'
                }
              `}
            >
              Активные
            </button>
            <button
              onClick={() => {
                setEventFilter('archived');
                hapticFeedback('light');
              }}
              className={`
                flex-1 px-3 py-2 rounded-lg font-medium text-sm
                transition-all duration-200 active:scale-[0.95]
                ${eventFilter === 'archived'
                  ? 'bg-blue-500 text-white shadow-md font-semibold'
                  : 'bg-white text-gray-700 border border-gray-200'
                }
              `}
            >
              Архив
            </button>
          </div>
        )}

        {/* 3. Задачи на выбранную дату */}
        <Card className="p-4">
          <div className="mb-3">
            <h3 className="font-semibold text-tg-text text-sm">
              {format(selectedDate, 'd MMMM yyyy', { locale: ru })}
            </h3>
          </div>
          
          {getTasksForDate(selectedDate).length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">Нет дел</p>
              <p className="text-xs text-gray-400 mt-1">
                Дела генерируются автоматически по правилам чек-листов
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {getTasksForDate(selectedDate).map((inst) => (
                <div 
                  key={inst.id} 
                  className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg"
                >
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{inst.rule?.text || 'Дело'}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {format(new Date(inst.dueAt), 'HH:mm', { locale: ru })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* 4. Фильтры */}
        <div 
          className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide -mx-4 px-4"
          style={{ 
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x mandatory',
            scrollPaddingLeft: '16px',
            scrollPaddingRight: '16px',
          }}
        >
        {(['all', 'today', 'overdue', 'pending', 'accepted', 'rejected', 'completed'] as FilterType[]).map((f) => (
          <button
            key={f}
            onClick={() => {
              logger.userAction('filter_click', { filter: f });
              setFilter(f);
              hapticFeedback('light');
            }}
            style={{ scrollSnapAlign: 'start' }}
            className={`
              whitespace-nowrap flex-shrink-0
              px-3 py-2 rounded-lg font-medium text-sm
              transition-all duration-200 active:scale-[0.95]
              min-h-[36px] touch-manipulation
              ${filter === f 
                ? 'bg-blue-500 text-white font-semibold shadow-md' 
                : 'bg-white text-gray-700 border border-gray-200 active:bg-gray-50'
              }
            `}
          >
            {f === 'all' && 'Все'}
            {f === 'today' && 'На сегодня'}
            {f === 'overdue' && 'Просрочено'}
            {f === 'pending' && 'Ожидает приёмки'}
            {f === 'accepted' && 'Принято'}
            {f === 'rejected' && 'Отклонено'}
            {f === 'completed' && 'Выполнено'}
          </button>
        ))}
        </div>

        {/* 5. Список чек-листов (в столбик) */}
        <div className="space-y-3">
        {(() => {
          if (assignmentsLoading) {
            return (
              <Card className="p-8 text-center">
                <LoadingSpinner size="lg" />
                <p className="text-tg-hint mt-4">Загрузка чек-листов...</p>
              </Card>
            );
          }
          
          if (!Array.isArray(assignments) || assignments.length === 0) {
            return (
              <Card className="p-8 text-center">
                <CheckSquare className="w-12 h-12 mx-auto mb-4 text-tg-hint" />
                <p className="text-tg-hint">Нет активных чек-листов</p>
                {activeTab === 'personal' && (
                  <p className="text-xs text-tg-hint mt-2">
                    Создайте личный чек-лист, нажав кнопку "Создать"
                  </p>
                )}
              </Card>
            );
          }

          const currentAssignments = getCurrentAssignments();

          if (!Array.isArray(currentAssignments) || currentAssignments.length === 0) {
            return (
              <Card className="p-8 text-center">
                <CheckSquare className="w-12 h-12 mx-auto mb-4 text-tg-hint" />
                <p className="text-tg-hint">Нет активных чек-листов</p>
                {activeTab === 'personal' && (
                  <p className="text-xs text-tg-hint mt-2">
                    Создайте личный чек-лист, нажав кнопку "Создать"
                  </p>
                )}
              </Card>
            );
          }
          
          return currentAssignments.map((assignment) => {
            const instances = getInstancesForAssignment(assignment.id);
            // Дополнительная дедупликация по ID и ruleId + plannedDate (на случай одинаковых экземпляров)
            const deduplicatedInstances = Array.from(
              new Map(
                instances.map((inst) => [
                  `${inst.id}-${inst.ruleId}-${new Date(inst.plannedDate).toISOString().split('T')[0]}`,
                  inst
                ])
              ).values()
            );
            const filteredInstances = filterInstances(deduplicatedInstances);
            const instancesToShow = filter === 'all' ? deduplicatedInstances : filteredInstances;
            const instancesCount = filter === 'all' ? deduplicatedInstances.length : filteredInstances.length;
            const templateRules = Array.isArray(assignment.template?.rules)
              ? [...assignment.template!.rules].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
              : [];
            
            // Функция для получения статуса дела
            const getStatusBadge = (instance: ChecklistItemInstance) => {
              const isOverdue = isChecklistInstanceOverdue(instance);
              let variant: 'default' | 'success' | 'warning' | 'danger' = 'default';
              let label = '';
              
              switch (instance.status) {
                case 'ASSIGNED':
                  label = 'Назначена';
                  variant = 'default';
                  break;
                case 'IN_PROGRESS':
                  label = 'В работе';
                  variant = 'default';
                  break;
                case 'COMPLETED':
                  label = 'Выполнена';
                  variant = 'success';
                  break;
                case 'COMPLETED_PENDING':
                  label = 'Ожидает приёмки';
                  variant = 'warning';
                  break;
                case 'ACCEPTED':
                  label = 'Принята';
                  variant = 'success';
                  break;
                case 'REJECTED':
                  label = 'Отклонена';
                  variant = 'danger';
                  break;
                default:
                  label = 'Неизвестно';
              }
              
              return { variant, label, isOverdue };
            };

            const getRuleRecurrenceLabel = (recurrence: any) => {
              switch (recurrence) {
                case 'NO_DATE':
                  return 'Без даты (входящие)';
                case 'DAILY':
                  return 'Ежедневно';
                case 'WEEKLY':
                  return 'Еженедельно';
                case 'MONTHLY':
                  return 'Ежемесячно';
                case 'ONCE':
                  return 'Разово';
                default:
                  return 'По расписанию';
              }
            };
            
            return (
              <Card 
                key={assignment.id} 
                className="p-4"
              >
                {/* Заголовок чек-листа */}
                <div 
                  className="cursor-pointer hover:bg-gray-50 transition-colors rounded-lg p-2 -m-2 mb-2"
                  onClick={() => {
                    hapticFeedback('light');
                    navigate(`/checklists/${assignment.templateId}/edit`);
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-tg-text">{assignment.template?.title}</h3>
                      {assignment.template?.description && (
                        <p className="text-sm text-tg-hint mt-1">{assignment.template.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {instancesCount > 0 && (
                        <Badge variant="info">{instancesCount}</Badge>
                      )}
                      <Badge variant={assignment.status === 'ACTIVE' ? 'success' : 'default'}>
                        {assignment.status === 'ACTIVE' ? 'Активен' : 'Архив'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-3 text-sm text-tg-hint">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {format(new Date(assignment.activeFrom), 'dd.MM.yyyy', { locale: ru })} -{' '}
                        {assignment.activeTo
                          ? format(new Date(assignment.activeTo), 'dd.MM.yyyy', { locale: ru })
                          : '∞'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-end mt-2">
                    <ChevronRight className="w-4 h-4 text-tg-hint" />
                  </div>
                </div>
                
                {/* Список дел - показываем ВСЕГДА */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-tg-text mb-3">Дела:</h4>
                  {/* Показываем экземпляры, если они есть */}
                  {instancesToShow.length > 0 ? (
                    <div className="space-y-2">
                      {instancesToShow.map((instance) => {
                        const statusInfo = getStatusBadge(instance);
                        return (
                          <div
                            key={instance.id}
                            className="p-3 bg-gray-50 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              hapticFeedback('light');
                              // Можно добавить навигацию на детали дела
                            }}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <p className="text-sm font-medium text-tg-text flex-1">
                                {instance.rule?.text || 'Дело без названия'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge variant={statusInfo.variant} className="text-xs">
                                {statusInfo.label}
                              </Badge>
                              {statusInfo.isOverdue && (
                                <Badge variant="danger" className="text-xs">
                                  Просрочено
                                </Badge>
                              )}
                              {instance.rule?.recurrence && instance.rule.recurrence !== 'NO_DATE' && (
                                <Badge variant="default" className="text-xs">
                                  {instance.rule.recurrence === 'DAILY' && 'Ежедневно'}
                                  {instance.rule.recurrence === 'WEEKLY' && 'Еженедельно'}
                                  {instance.rule.recurrence === 'MONTHLY' && 'Ежемесячно'}
                                  {instance.rule.recurrence === 'ONCE' && 'Разово'}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2 text-xs text-tg-hint">
                              <Clock className="w-3 h-3" />
                              <span>
                                {format(new Date(instance.plannedDate), 'dd.MM.yyyy', { locale: ru })} в{' '}
                                {format(new Date(instance.dueAt), 'HH:mm', { locale: ru })}
                              </span>
                            </div>
                            {instance.completedAt && (
                              <div className="flex items-center gap-2 mt-1 text-xs text-green-600">
                                <CheckCircle2 className="w-3 h-3" />
                                <span>
                                  Выполнено: {format(new Date(instance.completedAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : templateRules.length > 0 && instances.length === 0 ? (
                    // Показываем правила только если нет экземпляров
                    <div className="space-y-2">
                      {templateRules.map((rule) => {
                        const subtasks = Array.isArray(rule.subtasks)
                          ? [...rule.subtasks].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                          : [];
                        return (
                          <div key={rule.id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-medium text-tg-text flex-1">
                                {rule.text || 'Дело без названия'}
                              </p>
                            </div>

                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge variant="default" className="text-xs">
                                {getRuleRecurrenceLabel(rule.recurrence)}
                              </Badge>
                              {rule.requiresApproval && (
                                <Badge variant="warning" className="text-xs">
                                  Нужна приёмка
                                </Badge>
                              )}
                              {rule.priority && (
                                <Badge variant="info" className="text-xs">
                                  {rule.priority === 'CRITICAL' && 'Критично'}
                                  {rule.priority === 'HIGH' && 'Важно'}
                                  {rule.priority === 'CURRENT' && 'Текущее'}
                                </Badge>
                              )}
                            </div>

                            {subtasks.length > 0 && (
                              <div className="mt-2 text-xs text-tg-hint">
                                <p className="font-medium">Чек-пункты:</p>
                                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                                  {subtasks.map((st) => (
                                    <li key={st.id}>{st.text}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-tg-hint">
                        {instances.length === 0 
                          ? 'Дела будут генерироваться автоматически по правилам чек-листа'
                          : 'Нет дел, соответствующих выбранному фильтру'}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            );
          });
        })()}
        </div>
      </div>
    </div>
  );

  // Показываем загрузку, пока определяем статус менеджера
  if (projects === undefined || isManager === undefined) {
    return (
      <div className="pb-24 bg-white min-h-screen">
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="pb-24 bg-white min-h-screen">
      {/* Контент разделов */}
      {mainSection === 'my' && renderMyChecklists()}
      {mainSection === 'subordinates' && (
        <div className="px-4 pt-4 bg-white min-h-screen">
          {renderSectionTabs()}
          {isManager ? (
            <ErrorBoundary>
              <SubordinatesChecklists />
            </ErrorBoundary>
          ) : (
            <div className="p-8 text-center bg-white">
              <User className="w-12 h-12 mx-auto mb-4 text-tg-hint" />
              <p className="text-tg-hint">Доступно только для руководителей</p>
            </div>
          )}
        </div>
      )}
      {mainSection === 'approvals' && (
        <div className="px-4 pt-4">
          {renderSectionTabs()}
          {isManager ? (
            <ChecklistApprovalsPage />
          ) : (
            <div className="p-8 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-tg-hint" />
              <p className="text-tg-hint">Доступно только для руководителей</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

